import { normalizeLanguageCode } from 'src/utils/language-util';

export function remToPx(value: string) {
  return Math.round(parseFloat(value) * 16);
}

export function pxToRem(value: number) {
  return `${value / 16}rem`;
}

export function responsiveFontSizes({ sm, md, lg }: { sm: number; md: number; lg: number }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  };
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    fontSecondaryFamily: React.CSSProperties['fontFamily'];
    fontWeightSemiBold: React.CSSProperties['fontWeight'];
  }
}

export const primaryFont = 'Pretendard Variable, Pretendard, Public Sans, sans-serif';
export const secondaryFont = 'Pretendard Variable, Pretendard, Barlow, sans-serif';

export const languageFontMap: Record<string, string> = {
  ar: 'Noto Naskh Arabic, Pretendard Variable, Pretendard, sans-serif', // Arabic
  bn: 'Hind Siliguri, Pretendard Variable, Pretendard, sans-serif', // Bengali
  'zh-CN': 'Noto Sans SC, Pretendard Variable, Pretendard, sans-serif', // Simplified Chinese
  'zh-TW': 'Noto Sans TC, Pretendard Variable, Pretendard, sans-serif', // Traditional Chinese
  he: 'Noto Sans Hebrew, Pretendard Variable, Pretendard, sans-serif', // Hebrew
  ne: 'Noto Sans Devanagari, Pretendard Variable, Pretendard, sans-serif', // Nepali
  hi: 'Noto Sans Devanagari, Pretendard Variable, Pretendard, sans-serif', // Hindi
  th: 'Noto Sans Thai, Pretendard Variable, Pretendard, sans-serif', // Thai
  tr: 'Noto Sans, Pretendard Variable, Pretendard, sans-serif', // Turkish
  km: 'Noto Sans Khmer, Pretendard Variable, Pretendard, sans-serif', // Khmer
};

export function getFontFamilyByLang(lang: string) {
  const normalizedLang = normalizeLanguageCode(lang);
  return languageFontMap[normalizedLang] ?? primaryFont;
}

// ----------------------------------------------------------------------

export const createTypography = (lang?: string) => ({
  fontFamily: getFontFamilyByLang(lang ?? 'ko'),
  fontSecondaryFamily: secondaryFont,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  h1: {
    fontWeight: 700,
    lineHeight: 80 / 64,
    fontSize: pxToRem(40),
    fontFamily: secondaryFont,
    ...responsiveFontSizes({ sm: 40, md: 52, lg: 64 }),
  },
  h2: {
    fontWeight: 700,
    lineHeight: 64 / 48,
    fontSize: pxToRem(32),
    fontFamily: secondaryFont,
    ...responsiveFontSizes({ sm: 32, md: 36, lg: 48 }),
  },
  h3: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(24),
    fontFamily: secondaryFont,
    ...responsiveFontSizes({ sm: 24, md: 28, lg: 32 }),
  },
  h4: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(20),
    fontFamily: secondaryFont,
    ...responsiveFontSizes({ sm: 20, md: 22, lg: 24 }),
  },
  h5: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(18),
    fontFamily: secondaryFont,
    ...responsiveFontSizes({ sm: 18, md: 19, lg: 20 }),
  },
  h6: {
    fontWeight: 600,
    lineHeight: 28 / 18,
    fontSize: pxToRem(16),
    fontFamily: secondaryFont,
    ...responsiveFontSizes({ sm: 16, md: 17, lg: 18 }),
  },
  // Subtitle 1: 16px (lineHeight 기존 유지)
  subtitle1: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  // Subtitle 2: 14px (lineHeight 기존 유지)
  subtitle2: {
    fontWeight: 600,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  // Body 1: 16px (lineHeight 기존 유지)
  body1: {
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  // Body 2: 14px (lineHeight 기존 유지)
  body2: {
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  // Caption: 12px (lineHeight 기존 유지)
  caption: {
    lineHeight: 1.5,
    fontSize: pxToRem(12),
  },
  // Overline: 12px, 대문자 변환 (lineHeight 기존 유지)
  overline: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    textTransform: 'uppercase',
  },
  button: {
    fontWeight: 700,
    lineHeight: 24 / 14,
    fontSize: pxToRem(14),
    textTransform: 'capitalize',
  },
  buttonSmall: {
    fontWeight: 700,
    lineHeight: 22 / 13,
    fontSize: pxToRem(13),
  },
  buttonMedium: {
    fontWeight: 700,
    lineHeight: 24 / 14,
    fontSize: pxToRem(14),
  },
  buttonLarge: {
    fontWeight: 700,
    lineHeight: 26 / 15,
    fontSize: pxToRem(15),
  },
  labelText: {
    fontWeight: 700,
    lineHeight: 20 / 12,
    fontSize: pxToRem(12),
  },
  avatarGroup: {
    fontWeight: 400,
    lineHeight: 22 / 13,
    fontSize: pxToRem(13),
  },
  carouselText: {
    fontWeight: 400,
    lineHeight: 36 / 20,
    fontSize: pxToRem(20),
  },
  settingLabel: {
    fontWeight: 600,
    lineHeight: 18 / 12,
    fontSize: pxToRem(12),
  },
  chartLegend: {
    fontWeight: 400,
    lineHeight: 22 / 13,
    fontSize: pxToRem(13),
  },
  chipLabel: {
    fontWeight: 400,
    lineHeight: 18 / 13,
    fontSize: pxToRem(13),
  },
  tableHead: {
    fontWeight: 600,
    lineHeight: 24 / 14,
    fontSize: pxToRem(14),
  },
  inputLabel: {
    fontWeight: 600,
    lineHeight: 1,
    fontSize: pxToRem(12),
  },
  navSubHeader: {
    fontWeight: 700,
    lineHeight: 18 / 11,
    fontSize: pxToRem(11),
  },
  navItemDefault: {
    fontWeight: 400,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  navItemActive: {
    fontWeight: 600,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  navItemMiniDefault: {
    fontWeight: 400,
    lineHeight: 16 / 10,
    fontSize: pxToRem(10),
  },
  navItemMiniActive: {
    fontWeight: 600,
    lineHeight: 16 / 10,
    fontSize: pxToRem(10),
  },
});

// 기본 typography (기존 호환성을 위해)
export const typography = createTypography();
